import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  Row,
  Col,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { fetchUserProfile, updateUserProfile } from "model/UserInfoModel";
import "./profile-modal.scss";

const emptyForm = {
  name: "",
  email: "",
  oldPassword: "",
  password: "",
  rePassword: "",
  userImageUrl: "",
  userImageFile: null,
};

const buildUserImageInlineUrl = (userImageUrl = "") => {
  const raw = String(userImageUrl ?? "").trim();
  if (!raw) return "";
  if (/^data:|^blob:/i.test(raw)) return raw;
  if (/\/inline\/?$/i.test(raw)) return raw.replace(/\/$/, "");
  return `${raw.replace(/\/$/, "")}/inline`;
};

const isSuccessResponse = (res) =>
  res?.success !== false && (res?.success || res?.isSuccess || res?.data != null);

const ProfileModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ ...emptyForm });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);

  const resetForm = () => {
    setFormData({ ...emptyForm });
    setErrors({});
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const closeModal = () => {
    resetForm();
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const response = await fetchUserProfile();
        if (cancelled) return;

        const data = response?.data ?? response;
        if (!data || response?.success === false) {
          toast.error(response?.message || "Unable to load profile");
          return;
        }

        setFormData({
          name: String(data?.name ?? data?.Name ?? "").trim(),
          email: String(data?.email ?? data?.Email ?? "").trim(),
          oldPassword: "",
          password: "",
          rePassword: "",
          userImageUrl: String(
            data?.userImageUrl ??
              data?.UserImageUrl ??
              data?.profileImageUrl ??
              data?.imageUrl ??
              data?.userImage ??
              ""
          ).trim(),
          userImageFile: null,
        });
        setErrors({});
        setAvatarPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      setAvatarPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, userImageFile: file }));
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const validate = () => {
    const nextErrors = {};
    const name = String(formData.name || "").trim();
    const oldPassword = String(formData.oldPassword || "");
    const password = String(formData.password || "");
    const rePassword = String(formData.rePassword || "");
    const anyPassword =
      oldPassword.trim() || password.trim() || rePassword.trim();

    if (!name) {
      nextErrors.name = "Name is required";
    }

    if (anyPassword) {
      if (!oldPassword.trim()) {
        nextErrors.oldPassword = "Old password is required";
      }
      if (!password.trim()) {
        nextErrors.password = "New password is required";
      }
      if (!rePassword.trim()) {
        nextErrors.rePassword = "Re-password is required";
      } else if (password !== rePassword) {
        nextErrors.rePassword = "Passwords do not match";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || saving) return;

    const fd = new FormData();
    fd.append("Name", String(formData.name || "").trim());
    fd.append("OldPassword", String(formData.oldPassword || ""));
    fd.append("Password", String(formData.password || ""));
    fd.append("RePassword", String(formData.rePassword || ""));

    if (formData.userImageFile instanceof File) {
      fd.append("UserImageFile", formData.userImageFile);
    } else {
      fd.append("UserImageFile", "");
    }

    setSaving(true);
    try {
      const response = await updateUserProfile(fd);
      if (isSuccessResponse(response)) {
        toast.success(response?.message || "Profile updated successfully");
        closeModal();
        return;
      }
      toast.error(response?.message || "Unable to update profile");
    } finally {
      setSaving(false);
    }
  };

  const displayAvatarUrl =
    avatarPreviewUrl || buildUserImageInlineUrl(formData.userImageUrl);

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      backdrop="static"
      centered
      className="modal-full-height"
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-uppercase">Profile</h5>
          <Button close onClick={closeModal} />
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup className="text-center mb-4">
              <div className="profile-modal__avatar-wrap mx-auto">
                <div className="profile-modal__avatar">
                  {displayAvatarUrl ? (
                    <img src={displayAvatarUrl} alt={formData.name || "Profile"} />
                  ) : (
                    <i className="fa fa-user" aria-hidden="true" />
                  )}
                </div>
                <label
                  htmlFor="profile-user-image-input"
                  className="profile-modal__camera-btn mb-0"
                  title="Change photo"
                >
                  <i className="fa fa-camera" aria-hidden="true" />
                </label>
                <input
                  id="profile-user-image-input"
                  type="file"
                  accept="image/*"
                  className="d-none"
                  onChange={handleImageChange}
                />
              </div>
            </FormGroup>

            <FormGroup className="mb-3">
              <Row className="align-items-center g-2">
                <Col xs={12} sm={3}>
                  <Label className="mb-0">
                    Name <span className="text-danger">*</span>
                  </Label>
                </Col>
                <Col xs={12} sm={9}>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    invalid={Boolean(errors.name)}
                  />
                  {errors.name ? (
                    <small className="text-danger">{errors.name}</small>
                  ) : null}
                </Col>
              </Row>
            </FormGroup>

            <FormGroup className="mb-3">
              <Row className="align-items-center g-2">
                <Col xs={12} sm={3}>
                  <Label className="mb-0">Email</Label>
                </Col>
                <Col xs={12} sm={9}>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                    readOnly
                  />
                </Col>
              </Row>
            </FormGroup>

            <hr className="my-3" />

            <p className="text-primary small mb-3">
              If you don&apos;t want to change password leave this section blank.
            </p>

            <FormGroup className="mb-3">
              <Row className="align-items-center g-2">
                <Col xs={12} sm={3}>
                  <Label className="mb-0">Old Password</Label>
                </Col>
                <Col xs={12} sm={9}>
                  <Input
                    type="password"
                    name="oldPassword"
                    placeholder="Old Password"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    autoComplete="current-password"
                    invalid={Boolean(errors.oldPassword)}
                  />
                  {errors.oldPassword ? (
                    <small className="text-danger">{errors.oldPassword}</small>
                  ) : null}
                </Col>
              </Row>
            </FormGroup>

            <FormGroup className="mb-3">
              <Row className="align-items-center g-2">
                <Col xs={12} sm={3}>
                  <Label className="mb-0">New Password</Label>
                </Col>
                <Col xs={12} sm={9}>
                  <Input
                    type="password"
                    name="password"
                    placeholder="New Password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="new-password"
                    invalid={Boolean(errors.password)}
                  />
                  {errors.password ? (
                    <small className="text-danger">{errors.password}</small>
                  ) : null}
                </Col>
              </Row>
            </FormGroup>

            <FormGroup className="mb-3">
              <Row className="align-items-center g-2">
                <Col xs={12} sm={3}>
                  <Label className="mb-0">Re-Password</Label>
                </Col>
                <Col xs={12} sm={9}>
                  <Input
                    type="password"
                    name="rePassword"
                    placeholder="Re-Password"
                    value={formData.rePassword}
                    onChange={handleChange}
                    autoComplete="new-password"
                    invalid={Boolean(errors.rePassword)}
                  />
                  {errors.rePassword ? (
                    <small className="text-danger">{errors.rePassword}</small>
                  ) : null}
                </Col>
              </Row>
            </FormGroup>

            <div className="text-end mt-4">
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-1" aria-hidden="true" />
                    Updating...
                  </>
                ) : (
                  <>
                    {/* <i className="fa fa-save me-1" aria-hidden="true" /> */}
                    Update Profile
                  </>
                )}
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

ProfileModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default ProfileModal;
